import { useState, useEffect, useRef } from "react";
import { useMode } from "../context/ModeContext";
import { Blocks, Code2 } from "lucide-react";
import { motion } from "framer-motion";
import { Trophy, Medal, Crown, User, Calendar, Gamepad2, Code, ChevronLeft, ChevronRight, Pencil, Keyboard } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { MODES, MODE_META, modeLabel, normalizeMode } from "../lib/modes";

// Use empty string for same-origin API calls (works on both preview and production)
const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export default function Rankings({ user }) {
  const { mode: globalMode } = useMode();
  // Which leaderboard is shown; starts on the site mode and can be switched under the banner without changing the site mode
  const [siteMode, setSiteMode] = useState(normalizeMode(globalMode));
  const [activeTab, setActiveTab] = useState("all-time");
  const [allTimeData, setAllTimeData] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [flappyBatData, setFlappyBatData] = useState(null);
  const [typingData, setTypingData] = useState(null);
  const [juniorTypingData, setJuniorTypingData] = useState(null);
  const [topCreatorsData, setTopCreatorsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allTimePage, setAllTimePage] = useState(1);
  const [weeklyPage, setWeeklyPage] = useState(1);
  const [creatorsPage, setCreatorsPage] = useState(1);

  useEffect(() => {
    fetchAllRankings();
  }, []);

  useEffect(() => { setSiteMode(normalizeMode(globalMode)); }, [globalMode]);

  // Switching the leaderboard mode reloads both score boards from page 1 (mount is covered by fetchAllRankings)
  const firstMode = useRef(true);
  useEffect(() => {
    if (firstMode.current) { firstMode.current = false; return; }
    setAllTimePage(1);
    setWeeklyPage(1);
    setCreatorsPage(1);
    fetchAllTimeRankings(1);
    fetchWeeklyRankings(1);
    fetchTopCreators(1);
  }, [siteMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAllTimeRankings(allTimePage);
  }, [allTimePage]);

  useEffect(() => {
    fetchWeeklyRankings(weeklyPage);
  }, [weeklyPage]);

  useEffect(() => {
    fetchTopCreators(creatorsPage);
  }, [creatorsPage]);

  const fetchAllRankings = async () => {
    setLoading(true);
    try {
      const [allTime, weekly, flappyBat, typing, juniorTyping, topCreators] = await Promise.all([
        fetch(`${API}/rankings/all-time?page=1&per_page=50&mode=${siteMode}`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/rankings/weekly?page=1&per_page=50&mode=${siteMode}`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/rankings/flappy-bat`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/rankings/typing?category=code`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/rankings/typing?category=junior`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/rankings/top-creators?page=1&per_page=50&mode=${siteMode}`, { credentials: "include" }).then(r => r.json())
      ]);
      
      setAllTimeData(allTime);
      setWeeklyData(weekly);
      setFlappyBatData(flappyBat);
      setTypingData(typing);
      setJuniorTypingData(juniorTyping);
      setTopCreatorsData(topCreators);
    } catch (error) {
      console.error("Error fetching rankings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTimeRankings = async (page) => {
    try {
      const response = await fetch(`${API}/rankings/all-time?page=${page}&per_page=50&mode=${siteMode}`, { credentials: "include" });
      const data = await response.json();
      setAllTimeData(data);
    } catch (error) {
      console.error("Error fetching all-time rankings:", error);
    }
  };

  const fetchWeeklyRankings = async (page) => {
    try {
      const response = await fetch(`${API}/rankings/weekly?page=${page}&per_page=50&mode=${siteMode}`, { credentials: "include" });
      const data = await response.json();
      setWeeklyData(data);
    } catch (error) {
      console.error("Error fetching weekly rankings:", error);
    }
  };

  const fetchTopCreators = async (page) => {
    try {
      const response = await fetch(`${API}/rankings/top-creators?page=${page}&per_page=50&mode=${siteMode}`, { credentials: "include" });
      const data = await response.json();
      setTopCreatorsData(data);
    } catch (error) {
      console.error("Error fetching top creators:", error);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-medium text-muted-foreground">#{rank}</span>;
  };

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return "bg-yellow-500/20 text-yellow-500 border-yellow-500/30";
    if (rank === 2) return "bg-gray-400/20 text-gray-400 border-gray-400/30";
    if (rank === 3) return "bg-amber-600/20 text-amber-600 border-amber-600/30";
    return "bg-muted text-muted-foreground border-border";
  };

  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex items-center justify-center gap-2 mt-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="gap-1"
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </Button>
        
        <div className="flex items-center gap-1">
          {/* Show page numbers */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                className="w-8 h-8 p-0"
              >
                {pageNum}
              </Button>
            );
          })}
          
          {totalPages > 5 && currentPage < totalPages - 2 && (
            <>
              <span className="text-muted-foreground">...</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(totalPages)}
                className="w-8 h-8 p-0"
              >
                {totalPages}
              </Button>
            </>
          )}
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="gap-1"
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  const RankingList = ({ data, scoreLabel = "pts", currentPage, totalPages, onPageChange, detail, notRankedHint }) => {
    if (!data) return null;

    const { rankings, user_rank, total_ranked } = data;

    return (
      <div className="space-y-4">
        {/* User's own rank */}
        {user_rank && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-xl bg-primary/10 border border-primary/20"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getRankBadgeColor(user_rank.rank)}`}>
                  {getRankIcon(user_rank.rank)}
                </div>
                <div>
                  <p className="font-semibold text-foreground flex items-center gap-2">
                    {user_rank.nickname}
                    <Badge variant="outline" className="text-xs">You</Badge>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Rank #{user_rank.rank} of {total_ranked}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary">{(user_rank.score ?? user_rank.total_score ?? 0).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">{scoreLabel}{detail ? ` · ${detail(user_rank)}` : ""}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Not ranked message */}
        {user && !user_rank && (
          <div className="p-4 rounded-xl bg-muted/50 border border-border text-center">
            <p className="text-muted-foreground">
              {notRankedHint || "You're not ranked yet. Start answering questions to get on the leaderboard!"}
            </p>
          </div>
        )}

        {/* Total count and page info */}
        <div className="flex items-center justify-between px-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            {total_ranked} Players Ranked
          </h3>
          {totalPages > 1 && (
            <span className="text-xs text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
          )}
        </div>
        
        {/* Rankings list */}
        {rankings.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No rankings yet. Be the first!
          </div>
        ) : (
          <div className="space-y-1">
            {rankings.map((player, index) => (
              <motion.div
                key={player.user_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  user_rank && player.user_id === user_rank.user_id
                    ? "bg-primary/5 border border-primary/10"
                    : "hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    player.rank <= 3 ? getRankBadgeColor(player.rank) : ""
                  }`}>
                    {getRankIcon(player.rank)}
                  </div>
                  <div>
                    <p className="font-medium text-foreground flex items-center gap-2">
                      {player.nickname || 'Anonymous'}
                      {user_rank && player.user_id === user_rank.user_id && (
                        <Badge variant="outline" className="text-xs">You</Badge>
                      )}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold">{(player.score ?? player.total_score ?? 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{scoreLabel}{detail ? ` · ${detail(player)}` : ""}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {onPageChange && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />}
      </div>
    );
  };

  // Creators ranking list (shows question count instead of points)
  const CreatorsRankingList = ({ data, currentPage, totalPages, onPageChange }) => {
    if (!data) return null;

    const { rankings, user_rank, total_ranked } = data;

    return (
      <div className="space-y-4">
        {/* User's own rank */}
        {user_rank && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-xl bg-primary/10 border border-primary/20"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getRankBadgeColor(user_rank.rank)}`}>
                  {getRankIcon(user_rank.rank)}
                </div>
                <div>
                  <p className="font-semibold text-foreground flex items-center gap-2">
                    {user_rank.nickname}
                    <Badge variant="outline" className="text-xs">You</Badge>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Rank #{user_rank.rank} of {total_ranked}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary">{user_rank.question_count}</p>
                <p className="text-xs text-muted-foreground">questions</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Not ranked message */}
        {user && !user_rank && (
          <div className="p-4 rounded-xl bg-muted/50 border border-border text-center">
            <p className="text-muted-foreground">
              You haven't created any approved questions yet. Submit a question to get on this leaderboard!
            </p>
          </div>
        )}

        {/* Total count and page info */}
        <div className="flex items-center justify-between px-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            {total_ranked} Creators Ranked
          </h3>
          {totalPages > 1 && (
            <span className="text-xs text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
          )}
        </div>
        
        {/* Rankings list */}
        {rankings.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No creators yet. Be the first to create a question!
          </div>
        ) : (
          <div className="space-y-1">
            {rankings.map((creator, index) => (
              <motion.div
                key={creator.user_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  user_rank && creator.user_id === user_rank.user_id
                    ? "bg-primary/5 border border-primary/10"
                    : "hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    creator.rank <= 3 ? getRankBadgeColor(creator.rank) : ""
                  }`}>
                    {getRankIcon(creator.rank)}
                  </div>
                  <div>
                    <p className="font-medium text-foreground flex items-center gap-2">
                      {creator.nickname || 'CodingDrills Admin'}
                      {user_rank && creator.user_id === user_rank.user_id && (
                        <Badge variant="outline" className="text-xs">You</Badge>
                      )}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-foreground">{creator.question_count}</p>
                  <p className="text-xs text-muted-foreground">questions</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <Trophy className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2 flex-wrap">
            Leaderboard
            <span className="inline-flex items-center gap-1 text-sm font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground" data-testid="rankings-mode-badge">
              {(() => { const I = MODE_META[normalizeMode(siteMode)].icon; return <I className="w-3.5 h-3.5" />; })()}
              {modeLabel(siteMode)}
            </span>
          </h1>
          <p className="text-muted-foreground">See how you rank against other coders</p>
          <div className="mt-4 inline-flex items-center rounded-full border border-border bg-card p-1 shadow-sm" role="tablist" aria-label="Leaderboard mode" data-testid="rankings-mode-switch">
            {MODES.map((m) => {
              const on = siteMode === m;
              const Icon = MODE_META[m].icon;
              return (
                <button key={m} type="button" role="tab" aria-selected={on} onClick={() => setSiteMode(m)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${on ? `${MODE_META[m].pill} shadow` : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`rankings-mode-${m}`}>
                  <Icon className="w-3.5 h-3.5" /> {MODE_META[m].label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-2">All Time, Weekly and Creators boards follow this choice. Cave Bat and the Typing boards are shared across modes.</p>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-3xl mx-auto grid-cols-3 sm:grid-cols-6 gap-1 mb-6 h-auto">
            <TabsTrigger value="all-time" className="gap-1 text-xs sm:text-sm" data-testid="tab-all-time">
              <Code className="w-4 h-4 hidden sm:block" />
              <span>All Time</span>
            </TabsTrigger>
            <TabsTrigger value="weekly" className="gap-1 text-xs sm:text-sm" data-testid="tab-weekly">
              <Calendar className="w-4 h-4 hidden sm:block" />
              <span>Weekly</span>
            </TabsTrigger>
            <TabsTrigger value="creators" className="gap-1 text-xs sm:text-sm" data-testid="tab-creators">
              <Pencil className="w-4 h-4 hidden sm:block" />
              <span>Creators</span>
            </TabsTrigger>
            <TabsTrigger value="cave-bat" className="gap-1 text-xs sm:text-sm" data-testid="tab-cave-bat">
              <Gamepad2 className="w-4 h-4 hidden sm:block" />
              <span>Cave Bat</span>
            </TabsTrigger>
            <TabsTrigger value="typing" className="gap-1 text-xs sm:text-sm" data-testid="tab-typing">
              <Keyboard className="w-4 h-4 hidden sm:block" />
              <span>Typing</span>
            </TabsTrigger>
            <TabsTrigger value="junior-typing" className="gap-1 text-xs sm:text-sm" data-testid="tab-junior-typing">
              <Keyboard className="w-4 h-4 hidden sm:block" />
              <span>Junior Typing</span>
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              <TabsContent value="all-time">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-4 p-3 rounded-lg bg-muted/50 text-center">
                    <p className="text-sm text-muted-foreground">
                      Total coding points earned from all questions
                    </p>
                  </div>
                  <RankingList 
                    data={allTimeData} 
                    scoreLabel="pts" 
                    currentPage={allTimePage}
                    totalPages={allTimeData?.pagination?.total_pages || 1}
                    onPageChange={setAllTimePage}
                  />
                </motion.div>
              </TabsContent>

              <TabsContent value="weekly">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-4 p-3 rounded-lg bg-muted/50 text-center">
                    <p className="text-sm text-muted-foreground">
                      Points earned in the last 7 days (rolling window)
                    </p>
                    {weeklyData?.week && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {weeklyData.week}
                      </p>
                    )}
                  </div>
                  <RankingList 
                    data={weeklyData} 
                    scoreLabel="pts" 
                    currentPage={weeklyPage}
                    totalPages={weeklyData?.pagination?.total_pages || 1}
                    onPageChange={setWeeklyPage}
                  />
                </motion.div>
              </TabsContent>

              <TabsContent value="creators">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-4 p-3 rounded-lg bg-muted/50 text-center" data-testid="creators-rankings-blurb">
                    <p className="text-sm text-muted-foreground">
                      Who created the most approved {modeLabel(siteMode)} questions{topCreatorsData?.total_questions != null ? ` · ${topCreatorsData.total_questions.toLocaleString()} in total` : ""}
                    </p>
                  </div>
                  <CreatorsRankingList 
                    data={topCreatorsData}
                    currentPage={creatorsPage}
                    totalPages={topCreatorsData?.pagination?.total_pages || 1}
                    onPageChange={setCreatorsPage}
                  />
                </motion.div>
              </TabsContent>

              <TabsContent value="cave-bat">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-4 p-3 rounded-lg bg-muted/50 text-center">
                    <p className="text-sm text-muted-foreground">
                      🦇 Highest scores in the Cave Bat mini-game
                    </p>
                  </div>
                  <RankingList data={flappyBatData} scoreLabel="score" />
                </motion.div>
              </TabsContent>

              <TabsContent value="typing">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-4 p-3 rounded-lg bg-muted/50 text-center" data-testid="typing-rankings-blurb">
                    <p className="text-sm text-muted-foreground">
                      ⌨️ Best 30-second code Typing Sprint (Blocks & Python solutions) from live drills, 90%+ accuracy - ties go to the higher accuracy, then the earlier run
                    </p>
                  </div>
                  <RankingList data={typingData} scoreLabel="wpm" detail={(p) => `${p.accuracy ?? 0}% accuracy`} notRankedHint="You're not ranked yet. Solve a live drill question, then play the Typing Sprint while you wait!" />
                </motion.div>
              </TabsContent>

              <TabsContent value="junior-typing">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-4 p-3 rounded-lg bg-muted/50 text-center" data-testid="junior-typing-rankings-blurb">
                    <p className="text-sm text-muted-foreground">
                      ⌨️ Best 30-second word Typing Sprint from Junior Blocks live drills, 90%+ accuracy - ties go to the higher accuracy, then the earlier run
                    </p>
                  </div>
                  <RankingList data={juniorTypingData} scoreLabel="wpm" detail={(p) => `${p.accuracy ?? 0}% accuracy`} notRankedHint="You're not ranked yet. Solve a Junior Blocks live drill question, then play the Typing Sprint while you wait!" />
                </motion.div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
}
